import { useState, useEffect } from 'react';
import { usage as usageApi, services as svcApi } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';

export default function Usage() {
  const [usageData, setUsageData] = useState(null);
  const [hourlyData, setHourlyData] = useState(null);
  const [svcData, setSvcData] = useState({});

  useEffect(() => {
    usageApi.get().then(r => setUsageData(r.data)).catch(() => {});
    usageApi.hourly().then(r => setHourlyData(r.data)).catch(() => {});
    svcApi.list().then(r => setSvcData(r.data.services || {})).catch(() => {});
  }, []);

  const chartData = usageData?.by_service_24h?.map(u => ({
    service: svcData[u.service]?.name || u.service,
    requests: u.requests,
    latency: u.avg_latency_ms,
    tokens: u.tokens,
  })) || [];

  const tooltipStyle = { contentStyle: { background: '#0f172a', border: '1px solid #334155', borderRadius: '8px', fontSize: '12px' } };

  return (
    <div data-testid="usage-page" className="space-y-6">
      <div>
        <h2 className="text-3xl font-bold tracking-tight font-['Plus_Jakarta_Sans']">Usage Analytics</h2>
        <p className="text-muted-foreground mt-1">Monitor your API usage and performance</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-card border-border/50">
          <CardContent className="p-5">
            <p className="text-xs text-muted-foreground uppercase tracking-wider">Total Requests (24h)</p>
            <p data-testid="total-requests" className="text-3xl font-bold mt-1">{usageData?.total_requests_24h || 0}</p>
          </CardContent>
        </Card>
        <Card className="bg-card border-border/50">
          <CardContent className="p-5">
            <p className="text-xs text-muted-foreground uppercase tracking-wider">Services Used</p>
            <p className="text-3xl font-bold mt-1">{usageData?.by_service_24h?.length || 0}</p>
          </CardContent>
        </Card>
        <Card className="bg-card border-border/50">
          <CardContent className="p-5">
            <p className="text-xs text-muted-foreground uppercase tracking-wider">Avg Latency</p>
            <p className="text-3xl font-bold mt-1">
              {usageData?.by_service_24h?.length ? Math.round(usageData.by_service_24h.reduce((a, b) => a + b.avg_latency_ms, 0) / usageData.by_service_24h.length) : 0}ms
            </p>
          </CardContent>
        </Card>
      </div>

      {chartData.length > 0 && (
        <Card className="bg-card border-border/50">
          <CardHeader>
            <CardTitle className="text-lg">Requests by Service (24h)</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                <XAxis dataKey="service" tick={{ fill: '#94a3b8', fontSize: 11 }} />
                <YAxis tick={{ fill: '#94a3b8', fontSize: 11 }} />
                <Tooltip {...tooltipStyle} />
                <Bar dataKey="requests" fill="#6366f1" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      <Card className="bg-card border-border/50">
        <CardHeader>
          <CardTitle className="text-lg">Detailed Breakdown</CardTitle>
        </CardHeader>
        <CardContent>
          {!usageData?.by_service_24h?.length ? (
            <p className="text-center text-muted-foreground py-8 text-sm">No usage data yet. Make some API calls to see analytics.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border/50 text-muted-foreground">
                    <th className="text-left py-2 px-3 font-medium">Service</th>
                    <th className="text-right py-2 px-3 font-medium">Requests</th>
                    <th className="text-right py-2 px-3 font-medium">Avg Latency</th>
                    <th className="text-right py-2 px-3 font-medium">Tokens</th>
                  </tr>
                </thead>
                <tbody>
                  {usageData.by_service_24h.map(u => (
                    <tr key={u.service} className="border-b border-border/20">
                      <td className="py-2.5 px-3 font-medium">{svcData[u.service]?.name || u.service}</td>
                      <td className="py-2.5 px-3 text-right font-mono">{u.requests}</td>
                      <td className="py-2.5 px-3 text-right font-mono">{u.avg_latency_ms}ms</td>
                      <td className="py-2.5 px-3 text-right font-mono">{u.tokens}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
